const DEVICE_NAME_PREFIXES = ["dlgt", "hwqh", "dwlg"] as const;

const DAVEY_BLE_NAME_FILTERS = [
  { namePrefix: "dlgt" },
  { namePrefix: "hwqh" },
  { namePrefix: "dwlg" },
];

export function looksLikeDaveyBleName(name?: string | null): boolean {
  if (!name) return false;
  const lower = name.toLowerCase();
  return DEVICE_NAME_PREFIXES.some((prefix) => lower.startsWith(prefix));
}

/** Prefer live advertisement names over macOS-cached GAP names. */
export function pickBestBleName(
  candidates: Array<string | null | undefined>,
): string {
  for (const candidate of candidates) {
    const name = candidate?.trim();
    if (name && looksLikeDaveyBleName(name)) return name;
  }
  for (const candidate of candidates) {
    const name = candidate?.trim();
    if (name) return name;
  }
  return "";
}

function wait(ms: number) {
  return new Promise((resolve) => window.setTimeout(resolve, ms));
}

/**
 * macOS caches a stale GAP name on BluetoothDevice.name (e.g. old paired
 * serial) while the Chrome chooser shows the current advertising localName.
 *
 * Strategy:
 * 1. Listen for advertisements while the chooser is open (requestLEScan)
 * 2. Match by device.id when possible
 * 3. If only one Davey name was seen advertising, use that (common Mac case)
 * 4. Prefer any live ad name that differs from the stale cached GAP name
 * 5. Fall back to watchAdvertisements on the selected device
 */
export async function requestDaveyBleDevice(optionalServices: string[]): Promise<{
  device: BluetoothDevice;
  displayName: string;
} | null> {
  const bluetooth = navigator.bluetooth;
  const nameById = new Map<string, string>();
  const advertisedNames = new Set<string>();

  const onAdvertisement = (event: Event) => {
    const ad = event as BluetoothAdvertisingEvent;
    const name = ad.name?.trim();
    if (!name || !looksLikeDaveyBleName(name)) return;
    advertisedNames.add(name);
    if (ad.device?.id) nameById.set(ad.device.id, name);
  };

  bluetooth.addEventListener("advertisementreceived", onAdvertisement);

  let leScan: BluetoothLEScan | null = null;
  try {
    if (typeof bluetooth.requestLEScan === "function") {
      leScan = await bluetooth.requestLEScan({
        filters: DAVEY_BLE_NAME_FILTERS,
        keepRepeatedDevices: true,
      });
    }
  } catch (error) {
    console.warn("requestLEScan unavailable, falling back:", error);
  }

  try {
    const device = await bluetooth.requestDevice({
      filters: DAVEY_BLE_NAME_FILTERS,
      optionalServices,
    });

    if (!device) return null;

    // Late ads often arrive right after Pair on macOS.
    for (let i = 0; i < 10; i++) {
      if (nameById.has(device.id) || advertisedNames.size > 0) break;
      await wait(150);
    }

    const fromId = nameById.get(device.id);
    const cachedName = device.name?.trim() || "";
    const liveNames = Array.from(advertisedNames);
    const onlyLiveName = liveNames.length === 1 ? liveNames[0] : undefined;
    const liveDifferentFromCache = liveNames.find(
      (name) => name !== cachedName,
    );

    const fromWatch = await resolveBleDeviceName(device, 2000);
    const displayName = pickBestBleName([
      fromId,
      onlyLiveName,
      liveDifferentFromCache,
      fromWatch !== cachedName ? fromWatch : undefined,
      ...liveNames,
      fromWatch,
      cachedName,
    ]);

    console.log("BLE device name resolved:", {
      deviceId: device.id,
      cached: cachedName,
      fromId,
      advertisedNames: liveNames,
      fromWatch,
      displayName,
    });

    return { device, displayName };
  } finally {
    try {
      leScan?.stop();
    } catch {
      // ignore
    }
    bluetooth.removeEventListener("advertisementreceived", onAdvertisement);
  }
}

export async function resolveBleDeviceName(
  device: BluetoothDevice,
  timeoutMs = 2500,
): Promise<string> {
  const cachedName = device.name?.trim() || "";
  const watch = device.watchAdvertisements;

  if (typeof watch !== "function") {
    return cachedName;
  }

  return new Promise((resolve) => {
    let settled = false;
    let bestAdName = "";

    const finish = (name: string) => {
      if (settled) return;
      settled = true;
      window.clearTimeout(timer);
      device.removeEventListener("advertisementreceived", onAdvertisement);
      try {
        device.unwatchAdvertisements?.();
      } catch {
        // ignore
      }
      resolve(pickBestBleName([name, bestAdName, cachedName]));
    };

    const onAdvertisement = (event: Event) => {
      const adName = (event as BluetoothAdvertisingEvent).name?.trim();
      if (!adName) return;
      bestAdName = adName;
      if (looksLikeDaveyBleName(adName) && adName !== cachedName) {
        finish(adName);
      }
    };

    const timer = window.setTimeout(
      () => finish(bestAdName || cachedName),
      timeoutMs,
    );

    device.addEventListener("advertisementreceived", onAdvertisement);
    watch.call(device).catch((error: unknown) => {
      console.warn("watchAdvertisements failed, using cached name:", error);
      finish(bestAdName || cachedName);
    });
  });
}

export function extractSerialFromBleName(name?: string | null): string | null {
  if (!name) return null;
  const match = name.match(/(?:dlgt|hwqh|dwlg)[_-]?([A-Za-z0-9]+)/i);
  return match?.[1] ?? null;
}
